import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../utils/apiClient';
import { useSocket } from '../../../contexts/SocketContext';
import { attendanceMetric } from './studentHomeRules';

const REFRESH_AFTER_MS = 60 * 1000;

const EMPTY = {
  profile: { name: '', role: '', avatarUrl: '', active: true, email: '', phone: '', joinedAt: null },
  level: { current: 1, xp: 0, xpToNext: 100, totalXP: 0 },
  metrics: {
    courseProgress: { value: 0, label: '' },
    attendance: { value: 0, label: '' },
    aiAttempts: { value: 0, total: 0, label: '' },
    mockInterviews: { value: 0, label: '' },
  },
  courses: [],
  upcoming: [],
  streak: { days: 0, week: [] },
  performance: {},
  assignments: [],
  badges: [],
  aiInsight: null,
  recommendations: [],
};

const readPayload = (summary, mySummary) => {
  const metrics = { ...EMPTY.metrics, ...summary?.metrics };
  return {
    ...EMPTY,
    ...summary,
    metrics: { ...metrics, attendance: attendanceMetric(metrics.attendance, mySummary) },
  };
};

/**
 * The dashboard's data, kept current while the page is open: a class starting or
 * ending flips its course in place, and coming back to the tab reads again.
 *
 * `status` is `loading`, `ready` or `failed`. A failed FIRST read is `failed` — it
 * must not draw a dashboard of zeroes. A failed REFRESH keeps what is on screen
 * and sets `stale`, so the page can say so.
 *
 * `enabled` holds the first read until the plan is known; `attendance` says whether
 * the plan has My Attendance, whose endpoint answers 403 otherwise.
 */
export default function useStudentHome({ enabled = true, attendance = false } = {}) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [stale, setStale] = useState(false);
  const newest = useRef(0);
  const readAt = useRef(0);
  const hasData = useRef(false);
  const { socket } = useSocket() || {};

  const load = useCallback(async () => {
    newest.current += 1;
    const id = newest.current;
    try {
      const [summary, mySummary] = await Promise.all([
        apiClient.get('/student/me/dashboard-summary'),
        attendance ? apiClient.get('/attendance/my-summary').catch(() => null) : null,
      ]);
      if (id !== newest.current) return;
      setData(readPayload(summary?.data, mySummary?.data));
      hasData.current = true;
      readAt.current = Date.now();
      setStale(false);
      setStatus('ready');
    } catch {
      if (id !== newest.current) return;
      setStale(hasData.current);
      setStatus(hasData.current ? 'ready' : 'failed');
    }
  }, [attendance]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !hasData.current) return;
      if (Date.now() - readAt.current >= REFRESH_AFTER_MS) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  useEffect(() => {
    if (!socket) return undefined;
    const onNotice = (notice) => {
      if (!notice?.batchId) return;
      if (notice.type !== 'class_started' && notice.type !== 'class_ended') return;
      const isLive = notice.type === 'class_started';
      const batchId = String(notice.batchId);
      setData((prev) => {
        if (!prev) return prev;
        const courses = prev.courses.map((course) => (
          course.kind === 'batch' && String(course.batchId) === batchId ? { ...course, isLive } : course
        ));
        return { ...prev, courses };
      });
    };
    socket.on('notification', onNotice);
    return () => socket.off('notification', onNotice);
  }, [socket]);

  const retry = useCallback(() => {
    if (!hasData.current) setStatus('loading');
    return load();
  }, [load]);

  return { data, status, stale, retry };
}
